import { useCallback } from 'react';
import {
  ArrowLeft,
  ChevronRight,
  Scale,
  Lock,
  FileCheck,
  ShieldAlert,
  Users,
  Flag,
  Trash2,
  Cookie,
  Copyright,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { LEGAL_DOCUMENTS, LEGAL_VERSION, type LegalSlug } from '@/data/legalDocuments';
import { useHideHtmlPreloader } from '@/components/loading';

const DOC_ICONS: Record<LegalSlug, LucideIcon> = {
  'terms-of-service': Scale,
  'privacy-policy': Lock,
  eula: FileCheck,
  'acceptable-use-policy': ShieldAlert,
  'community-guidelines': Users,
  'content-moderation-policy': Flag,
  'data-retention-policy': Trash2,
  'cookie-policy': Cookie,
  'dmca-policy': Copyright,
};

export function LegalHubPage() {
  const navigate = useNavigate();

  // This route lives outside MainLayout/SpiritLoader, so nothing else hides the
  // static HTML preloader when it's the first page loaded (direct/deep link).
  useHideHtmlPreloader(true);

  // navigate(-1) can strand the user if this page was reached via a direct/deep
  // link with no prior in-app history (e.g. window.history.length === 1), so
  // fall back to a known-good destination instead of doing nothing.
  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [navigate]);

  return (
    <div className="h-screen bg-gradient-to-b from-[#63345e] to-[#8e4585] flex flex-col">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-4">
        <button
          onClick={handleBack}
          className="p-2 -ml-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-lg font-bold text-white font-['Merriweather']">Legal & Policies</h1>
      </div>

      {/* Content */}
      <div className="flex-1 bg-white rounded-t-3xl overflow-y-auto overscroll-contain">
        <div className="max-w-2xl mx-auto px-6 py-8 pb-[calc(2rem+env(safe-area-inset-bottom))]">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-slate-800 font-['Merriweather'] leading-snug mb-1">
              Legal Documents
            </h2>
            <p className="text-sm text-slate-500">
              These documents govern your use of Spirit of Shabbat. Tap any document to read the
              full text.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden mb-6">
            {LEGAL_DOCUMENTS.map((doc, i) => {
              const Icon = DOC_ICONS[doc.slug];
              return (
                <div key={doc.slug}>
                  <Link
                    to={`/legal/${doc.slug}`}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#f0e6f0] flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-[#8e4585]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-slate-800">{doc.title}</span>
                      <p className="text-xs text-slate-400 mt-0.5">{doc.shortDescription}</p>
                    </div>
                    <ChevronRight size={16} className="text-slate-300 shrink-0" />
                  </Link>
                  {i < LEGAL_DOCUMENTS.length - 1 && (
                    <div className="ml-[60px] mr-4 h-px bg-slate-100" />
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-center text-[10px] text-slate-300">Policy version {LEGAL_VERSION}</p>
        </div>
      </div>
    </div>
  );
}
